import { Lens } from "@hookform/lenses";
import { useFieldArray } from "@hookform/lenses/rhf";
import Delete from "@mui/icons-material/Delete";
import { Box, Button, IconButton, MenuItem } from "@mui/material";
import { useImperativeHandle } from "react";
import { useWatch } from "react-hook-form";

import { NgpVanDatabaseModeGqlType } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import RhfNgpVanActionField from "~frontend/components/forms/NgpVanObjectAutocomplete/RhfNgpVanActionField";
import { RhfSelect } from "~frontend/components/forms/react-hook-form-components";
import { palette } from "~frontend/theme";

import { IntegrationsType } from "./types";
import { emptyIntegration, NgpVanIntegration } from "./types";

export type EditIntegrationsListHandle = {
  addNewIntegration: () => void;
};

export function EditIntegrationsList({
  ref,
  lens,
  vanIntegrations,
  displayAddButton = false,
}: {
  ref?: React.RefObject<EditIntegrationsListHandle | null>;
  lens: Lens<IntegrationsType>;
  vanIntegrations: NgpVanIntegration[];
  displayAddButton?: boolean;
}): React.ReactElement {
  const { fields, update, remove, append } = useFieldArray(lens.interop());
  useImperativeHandle(ref, () => ({
    addNewIntegration: () => {
      append(emptyIntegration());
    },
  }));

  return (
    <>
      {lens.map(fields, (integrationValue, l, index) => (
        <Box
          key={integrationValue.id}
          sx={{
            borderBottom:
              index < fields.length - 1 ? `1px solid ${palette.lightGray}` : 0,
            mb: 1,
          }}
        >
          <IntegrationListItem
            key={integrationValue.id}
            index={index}
            lens={l}
            vanIntegrations={vanIntegrations}
            onReplace={newIdAndDbMode =>
              update(index, {
                ...emptyIntegration(),
                integrationIdAndDbMode: newIdAndDbMode,
              })
            }
            onRemove={() => remove(index)}
          />
        </Box>
      ))}
      {displayAddButton && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="tertiary"
            onClick={() => append(emptyIntegration())}
          >
            + Add A VAN Integration
          </Button>
        </Box>
      )}
    </>
  );
}

function IntegrationListItem({
  lens,
  index,
  vanIntegrations,
  onReplace,
  onRemove,
}: {
  lens: Lens<IntegrationsType[number]>;
  index: number;
  vanIntegrations: NgpVanIntegration[];
  onReplace: (newIdAndDbMode: string) => void;
  onRemove: () => void;
}): React.ReactElement {
  const value = useWatch(lens.interop());

  let integrationId: string | undefined;
  let databaseMode: NgpVanDatabaseModeGqlType | undefined;
  let integration: NgpVanIntegration | undefined;

  if (value.integrationIdAndDbMode) {
    [integrationId, databaseMode] = value.integrationIdAndDbMode.split("|") as [
      string,
      NgpVanDatabaseModeGqlType,
    ];

    integration = vanIntegrations.find(i => i.id === integrationId);
  }

  return (
    <>
      <FlexBox>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <RhfSelect
            {...lens.focus("integrationIdAndDbMode").interop()}
            fullWidth
            label={`VAN Integration ${index + 1}`}
            onChange={value => onReplace(value as string)}
          >
            {vanIntegrations.flatMap(integration => [
              ...(integration.metadata.hasMyVoters
                ? [
                    <MenuItem
                      key="myVoters"
                      value={`${integration.id}|${NgpVanDatabaseModeGqlType.MyVoters}`}
                    >
                      {`${integration.name} > MyVoters`}
                    </MenuItem>,
                  ]
                : []),
              ...(integration.metadata.hasMyCampaign
                ? [
                    <MenuItem
                      key="myCampaign"
                      value={`${integration.id}|${NgpVanDatabaseModeGqlType.MyCampaign}`}
                    >
                      {`${integration.name} > MyCampaign`}
                    </MenuItem>,
                  ]
                : []),
            ])}
          </RhfSelect>
        </Box>
        <Box>
          <IconButton
            onClick={onRemove}
            sx={{
              height: "40px",
              mt: 3,
              ml: 2,
            }}
          >
            <Delete />
          </IconButton>
        </Box>
      </FlexBox>
      {integration && integrationId && databaseMode && (
        <Box>
          <RhfNgpVanActionField
            databaseMode={databaseMode}
            integrationId={integrationId}
            integration={integration}
            lens={lens}
          />
        </Box>
      )}
    </>
  );
}
